import { WidgetDefinition } from '../../../../shared/types/widget';
import { Typography } from '../../../widgets/Typography';
import observerManager from '../services/ObserverManager';
import { motion } from 'motion/react';
import React, { Suspense } from 'react';
import WidgetSuspenseFallback from './WidgetSuspenseFallback';
import WidgetContent from './WidgetContent';
import { HEADER_IMPLEMENTATION_OWNERSHIP } from '../constants';
import useWidgetRegistry from '../hooks/useWidgetRegistry';
import { WIDGET_LOADING_VARIANTS } from '../animations';
import { useWidgetDownload } from '../../../../utilities/common/hooks/useWidgetDownload';
import WidgetKebab from '../../../widgets/WidgetKebab';
import { WidgetType } from '../../../../shared/constants/widget';
import { useFilterData } from '../../../../utilities/hooks/useFilterData';

type Props = {
  widget: WidgetDefinition;
  delayMultiplier?: number; // prop to determine the delay for staggered animation when multiple widgets are rendered together
  isGroupedByTabView?: boolean; // prop to indicate if the widget is inside a TabView group
};

/**
 * WidgetRenderer - Container component for viewport-based lazy loading
 *
 * 1. IMMEDIATE RENDER: The widget container (title, subtitle, placeholder) renders
 *    immediately without waiting for data, providing instant visual feedback.
 *
 * 2. VIEWPORT DETECTION: Using IntersectionObserver, we detect when the widget
 *    enters the viewport. Data fetching is deferred until this moment.
 *
 * 3. CONDITIONAL MOUNTING: WidgetContent only mounts when `isVisibleInViewport` is true.
 *    This is crucial because useSuspenseQuery doesn't support an `enabled` parameter -
 *    the only way to defer the query is to defer mounting the component that calls it.
 *
 * 4. SUSPENSE BOUNDARY: Once WidgetContent mounts, it triggers useSuspenseQuery which
 *    suspends rendering. The Suspense boundary catches this and shows the fallback
 *    loader while data is being fetched.
 *
 * Why this architecture?
 * - useSuspenseQuery guarantees data is available when the component renders (no loading states)
 * - Viewport-based loading reduces initial page load and network requests
 * - Each widget has its own Suspense boundary, so widgets load independently
 * - Separates concerns: WidgetRenderer handles visibility, WidgetContent handles data
 * - Supports render-as-you-fetch pattern for optimal UX
 */
export default function WidgetRenderer({
  widget,
  delayMultiplier = 0,
  isGroupedByTabView = false,
}: Props) {
  const { id, title, subtitle, width, type: widgetType } = widget;
  const { configData } = useFilterData();
  const { widgetDownload, loading: isDownloadLoading, ref } =
    useWidgetDownload();

  const disableExportImage = widgetType === WidgetType.HERO_DIMENSION;
  // Debug mode: Add a red border to the widget container for visual debugging of layout and viewport detection
  // also shows the widget ID for easier identification
  const isDebugMode = !!localStorage.getItem('debug-widget-renderer');

  const widgetRef = React.useRef<HTMLDivElement | null>(null);

  const [isVisibleInViewport, setIsVisibleInViewport] = React.useState(false);

  const { headerImplementationOwnership } = useWidgetRegistry({
    widgetType,
    isGroupedByTabView,
  });

  // Register this widget with the shared IntersectionObserver.
  // Once the widget enters the viewport, we set isVisibleInViewport to true,
  // which triggers mounting of WidgetContent and initiates data fetching.
  // Note: We only set to true (never back to false) to avoid re-fetching
  // when the user scrolls away and back.
  React.useEffect(() => {
    const element = widgetRef.current;
    if (element) {
      observerManager.observe(widget.id, element, () => {
        setIsVisibleInViewport(true);
      });
    }

    return () => {
      observerManager.unobserve(widget.id);
    };
  }, []);

  return (
    <motion.div
      variants={WIDGET_LOADING_VARIANTS}
      initial="hidden"
      animate="visible"
      custom={{
        delayMultiplier,
      }}
      ref={(node) => {
        widgetRef.current = node;
        ref.current = node;
      }}
      data-widget-id={id}
      className={`relative flex h-full flex-col rounded-2xl ${
        widgetType === WidgetType.HERO_DIMENSION
          ? 'bg-transparent'
          : 'border-taupe-600 border bg-white p-6 shadow-[0px_1px_2px_0px_#1018280D]'
      }`}
      style={{
        gridColumn: `span ${width}`,
      }}
    >
      {!disableExportImage &&
       <WidgetKebab
        className="!absolute top-4 right-4 z-10"
        widgetAction={{
          downloadWidget: () => widgetDownload(title, id),
          loading: isDownloadLoading,
        }}
      />}
      {headerImplementationOwnership ===
        HEADER_IMPLEMENTATION_OWNERSHIP.Wrapper &&
        (title || subtitle) && (
          <>
            <Typography
              variant="display-xs"
              className="mb-2 pr-4 font-bold! text-gray-900"
            >
              {title}
            </Typography>
            <Typography
              variant="body-sm"
              className="pb-6 text-pretty text-gray-700"
            >
              {subtitle}
            </Typography>
          </>
        )}
      {isDebugMode && (
        <Typography
          variant="body-sm"
          className="pb-6 text-pretty text-gray-700"
        >
          {widget.id}
        </Typography>
      )}
      {/* 
        Conditional rendering is the key to viewport-based suspense:
        
        - When NOT in viewport: Show placeholder. WidgetContent is NOT mounted,
          so useSuspenseQuery inside it is never called. No network request.
        
        - When IN viewport: Mount WidgetContent inside a Suspense boundary.
          useSuspenseQuery fires immediately, suspending the component.
          Suspense catches this and shows fallback while fetching.
          Once data arrives, WidgetContent renders with guaranteed data.
      */}
      {isVisibleInViewport && configData !== undefined ? (
        <Suspense
          fallback={
            <WidgetSuspenseFallback widgetType={widgetType} meta={widget} />
          }
        >
          <WidgetContent
            widget={widget}
            isGroupedByTabView={isGroupedByTabView}
          />
        </Suspense>
      ) : (
        <WidgetSuspenseFallback widgetType={widgetType} meta={widget} />
      )}
    </motion.div>
  );
}
